<template>
  <div class="ledger-page">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_maps_movements') }}
        </h4>
        <b-link :to="{ name: 'ledger-maps' }">
            {{ $t('ledger_maps_title') }}
        </b-link>
      </div>

      <b-row class="mb-1">
        <b-col
          cols="12"
          md="5"
        >
          <v-select
            v-model="filter.itemId"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="items"
            :reduce="option => option.id"
            :get-option-label="itemOption"
            :placeholder="$t('ledger_maps_filter_map')"
            class="ledger-account-select"
            @input="loadMovements"
          />
        </b-col>
        <b-col
          cols="12"
          md="7"
          class="d-flex flex-wrap mt-50 mt-md-0"
        >
          <b-button
            v-for="option in typeOptions"
            :key="String(option.value)"
            size="sm"
            class="mr-50 mb-50"
            :variant="filter.type === option.value ? 'primary' : 'outline-primary'"
            @click="setType(option.value)"
          >
            {{ option.text }}
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_maps_movements_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(movement_date)="data">
              {{ data.item.movement_date }}
            </template>
            <template #cell(map)="data">
              {{ movementMap(data.item) }}
            </template>
            <template #cell(type)="data">
              <b-badge :variant="typeVariant(data.item.type)">
                {{ typeText(data.item.type) }}
              </b-badge>
            </template>
            <template #cell(quantity)="data">
              <span class="ledger-num">{{ qty(data.item.quantity) }}</span>
            </template>
            <template #cell(unit_cost)="data">
              <span class="ledger-num">{{ money(data.item.unit_cost) }}</span>
            </template>
            <template #cell(total_cost)="data">
              <span class="ledger-num">{{ money(data.item.total_cost) }}</span>
            </template>
            <template #cell(journals)="data">
              <b-link
                v-if="journalId(data.item, 'revenue') && canOpenJournal"
                class="d-block"
                :to="journalRoute(journalId(data.item, 'revenue'))"
              >
                {{ $t('ledger_maps_revenue_journal') }}
              </b-link>
              <b-link
                v-if="journalId(data.item, 'cogs') && canOpenJournal"
                class="d-block"
                :to="journalRoute(journalId(data.item, 'cogs'))"
              >
                {{ $t('ledger_maps_cogs_journal') }}
              </b-link>
              <span v-if="!journalId(data.item, 'revenue') && !journalId(data.item, 'cogs')">—</span>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="row in rows"
            :key="row.id"
            class="ledger-map-card"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ movementMap(row) }}</strong>
              <b-badge :variant="typeVariant(row.type)">
                {{ typeText(row.type) }}
              </b-badge>
            </div>
            <div class="text-muted small">
              {{ row.movement_date }}
            </div>
            <div class="ledger-num mt-50">
              {{ qty(row.quantity) }} · {{ money(row.total_cost) }}
            </div>
          </article>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BBadge, BLink, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getMapStock, getMapStockMovements } from '@/api/accounting/mapInventory'
import {
  listFrom, enumValue, enumLabel, formatLedgerMoney, formatLedgerQty, apiErrorMessage,
} from '@/utils/ledger'

const TYPE_VARIANT = {
  opening: 'info',
  purchase: 'primary',
  sale: 'success',
  write_off: 'danger',
}

export default {
  name: 'MapMovementsList',
  components: {
    BCard, BRow, BCol, BButton, BTable, BBadge, BLink, BSpinner, vSelect,
  },
  data() {
    return {
      loading: false,
      rows: [],
      items: [],
      filter: {
        itemId: this.$route.query.cadastral_map_id ? Number(this.$route.query.cadastral_map_id) : null,
        type: null,
      },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    typeOptions() {
      return [
        { value: null, text: this.$t('ledger_maps_type_all') },
        { value: 'opening', text: this.$t('ledger_maps_type_opening') },
        { value: 'purchase', text: this.$t('ledger_maps_type_purchase') },
        { value: 'sale', text: this.$t('ledger_maps_type_sale') },
        { value: 'write_off', text: this.$t('ledger_maps_type_write_off') },
      ]
    },
    fields() {
      return [
        { key: 'movement_date', label: this.$t('ledger_date') },
        { key: 'map', label: this.$t('ledger_maps_number') },
        { key: 'type', label: this.$t('ledger_type') },
        { key: 'quantity', label: this.$t('ledger_maps_qty') },
        { key: 'unit_cost', label: this.$t('ledger_maps_unit_cost') },
        { key: 'total_cost', label: this.$t('ledger_maps_total_cost') },
        { key: 'journals', label: this.$t('ledger_journals_title') },
      ]
    },
  },
  mounted() {
    this.loadItems()
    this.loadMovements()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    qty(value) {
      return formatLedgerQty(value || 0, this.$i18n.locale)
    },
    itemOption(item) {
      if (!item) return ''
      const number = item.map_number || item.display_name || `#${item.id}`
      return item.display_name && item.map_number && item.display_name !== item.map_number
        ? `${item.map_number} — ${item.display_name}`
        : number
    },
    movementMap(row) {
      const map = row.cadastral_map
      if (map) return this.itemOption(map)
      return row.cadastral_map_id ? `#${row.cadastral_map_id}` : ''
    },
    typeText(type) {
      return enumLabel(type, this.$i18n.locale)
    },
    typeVariant(type) {
      return TYPE_VARIANT[enumValue(type)] || 'secondary'
    },
    journalId(row, kind) {
      if (kind === 'revenue') {
        return row.revenue_journal_entry_id || (row.revenue_journal_entry && row.revenue_journal_entry.id) || null
      }
      return row.cogs_journal_entry_id || (row.cogs_journal_entry && row.cogs_journal_entry.id) || null
    },
    journalRoute(id) {
      return { name: 'ledger-journals-view', params: { id } }
    },
    setType(value) {
      this.filter.type = value
      this.loadMovements()
    },
    toast(title) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'AlertCircleIcon', variant: 'danger' },
      })
    },
    async loadItems() {
      try {
        const res = await getMapStock()
        this.items = listFrom(res)
      } catch (error) {
        this.items = []
      }
    },
    async loadMovements() {
      this.loading = true
      try {
        const params = {}
        if (this.filter.itemId) params.cadastral_map_id = this.filter.itemId
        if (this.filter.type) params.type = this.filter.type
        const res = await getMapStockMovements(params)
        this.rows = listFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')))
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-map-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
}
</style>
